/** TDA Membership Portal — shared enums and reference data */

export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ASSOCIATION_ADMIN: 'association_admin',
  STAFF: 'staff',
  MEMBER: 'member',
  STUDENT: 'student',
  TREASURER: 'treasurer',
  CONFERENCE_USHER: 'conference_usher',
};

/** Legacy `admin` maps to association admin */
export const ADMIN_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ASSOCIATION_ADMIN,
  'admin',
  ROLES.STAFF,
  ROLES.TREASURER,
];

export const FINANCE_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ASSOCIATION_ADMIN,
  'admin',
  ROLES.TREASURER,
];

export const SCAN_PORTAL_ROLES = [
  ROLES.CONFERENCE_USHER,
  ...ADMIN_ROLES,
];

export const MEMBER_PORTAL_ROLES = [ROLES.MEMBER, ROLES.STUDENT];

export const MEMBERSHIP_TYPES = [
  { value: 'full_member', label: 'Full Member' },
  { value: 'associate', label: 'Associate' },
  { value: 'student', label: 'Student' },
  { value: 'honorary', label: 'Honorary' },
  { value: 'retired', label: 'Retired' },
];

export const MEMBERSHIP_STATUSES = [
  { value: 'pending_payment', label: 'Pending payment' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'suspended', label: 'Suspended' },
];

export const mapLegacyStatusToUnified = (status) => {
  const value = String(status || 'active').toLowerCase();
  if (value === 'inactive') return 'expired';
  if (value === 'suspended') return 'suspended';
  if (value === 'retired') return 'active';
  if (value === 'active') return 'active';
  return value;
};

export const mapUnifiedStatusToLegacy = (status) => {
  const value = String(status || '').toLowerCase();
  if (value === 'expired' || value === 'pending_payment') return 'inactive';
  if (value === 'suspended') return 'suspended';
  return 'active';
};

export const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

export const TANZANIA_REGIONS = [
  'Arusha',
  'Dar es Salaam',
  'Dodoma',
  'Geita',
  'Iringa',
  'Kagera',
  'Katavi',
  'Kigoma',
  'Kilimanjaro',
  'Lindi',
  'Manyara',
  'Mara',
  'Mbeya',
  'Morogoro',
  'Mtwara',
  'Mwanza',
  'Njombe',
  'Pwani',
  'Rukwa',
  'Ruvuma',
  'Shinyanga',
  'Simiyu',
  'Singida',
  'Songwe',
  'Tabora',
  'Tanga',
  'Zanzibar North',
  'Zanzibar South',
  'Zanzibar Urban/West',
];

export const SPECIALIZATIONS = [
  'General Dentistry',
  'Restorative Dentistry',
  'Orthodontics',
  'Periodontics',
  'Prosthodontics',
  'Oral and Maxillofacial Surgery',
  'Pediatric Dentistry',
  'Endodontics',
  'Oral Pathology',
  'Public Health Dentistry',
  'Dental Therapy',
  'Dental Hygiene',
  'Other',
];

export const DEFAULT_FEES_BY_TYPE = {
  full_member: 30000,
  associate: 30000,
  student: 30000,
  honorary: 0,
  retired: 30000,
};

export const DEFAULT_REGISTRATION_FEES_BY_TYPE = {
  full_member: 70000,
  associate: 70000,
  student: 0,
  honorary: 0,
  retired: 70000,
};

export const normalizeRole = (role) => {
  if (!role) return ROLES.MEMBER;
  const next = String(role).toLowerCase();
  if (next === 'admin') return ROLES.ASSOCIATION_ADMIN;
  const valid = Object.values(ROLES);
  return valid.includes(next) ? next : ROLES.MEMBER;
};

export const isAdminRole = (role) => ADMIN_ROLES.includes(normalizeRole(role));

export const isFinanceRole = (role) => FINANCE_ROLES.includes(normalizeRole(role));

export const isUsherRole = (role) => normalizeRole(role) === ROLES.CONFERENCE_USHER;

export const canAccessScanPortal = (role) =>
  SCAN_PORTAL_ROLES.includes(normalizeRole(role));

export const getMembershipTypeLabel = (value) =>
  MEMBERSHIP_TYPES.find((t) => t.value === value)?.label || value || '—';
